import { useState } from 'react';
import { Alert, App, Button, Card, Typography } from 'antd';
import { ApiError } from '@/lib/http/apiClient';
import { nocApi } from '../api/nocApi';
import { AlertsPanel } from '../components/AlertsPanel';
import { NocHero } from '../components/NocHero';
import { PowerChart } from '../components/PowerChart';
import { ProvisionalDataNotice } from '../components/ProvisionalDataNotice';
import { StationMap } from '../components/StationMap';
import { StationTable } from '../components/StationTable';
import { useNocLive } from '../hooks/useNocLive';

/**
 * Dashboard NOC (US 2.6, TG-96) — revenue-forward.
 *
 * Consume los endpoints reales `/api/v1/noc/*` y el stream SSE. Los ingresos son
 * **estimados** (energía × tarifa provisional): ver nota.
 */
export function NocPage() {
  const live = useNocLive();
  const { message } = App.useApp();
  const [selectedStationId, setSelectedStationId] = useState<string | null>(null);
  const [ackingId, setAckingId] = useState<string | null>(null);

  const acknowledge = async (alertId: string) => {
    setAckingId(alertId);
    try {
      await nocApi.ackAlert(alertId);
      message.success('Alerta acusada.');
      live.refresh();
    } catch (caught) {
      message.error(
        caught instanceof ApiError ? caught.message : 'No se pudo acusar la alerta.',
      );
    } finally {
      setAckingId(null);
    }
  };

  const focusStation = (stationId: string) => {
    setSelectedStationId(stationId);
    document.getElementById('noc-map')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const openAlerts = live.alerts.filter((alert) => alert.status === 'OPEN').length;

  return (
    <div className="noc-page">
      <NocHero
        overview={live.overview}
        sessions={live.sessions}
        mode={live.mode}
        lastUpdated={live.lastUpdated}
        loading={live.loading}
      />

      {live.error && (
        <Alert
          type="error"
          showIcon
          message={live.error}
          action={
            <Button size="small" onClick={live.refresh}>
              Reintentar
            </Button>
          }
        />
      )}

      <ProvisionalDataNotice />

      <div className="noc-grid noc-grid--split" id="noc-map">
        <Card
          title="Mapa en vivo"
          extra={<Typography.Text type="secondary">Colombia · OpenStreetMap</Typography.Text>}
        >
          <StationMap
            stations={live.stations}
            selectedStationId={selectedStationId}
            onSelectStation={setSelectedStationId}
          />
        </Card>
        <PowerChart data={live.powerHistory} />
      </div>

      <div className="noc-grid noc-grid--split">
        <Card
          title="Estaciones y conectores"
          extra={
            <Button size="small" onClick={live.refresh}>
              Actualizar
            </Button>
          }
        >
          <StationTable
            stations={live.stations}
            selectedStationId={selectedStationId}
            onSelectStation={focusStation}
          />
        </Card>
        <Card
          title="Alertas priorizadas"
          extra={<Typography.Text type="secondary">{openAlerts} abiertas</Typography.Text>}
        >
          <AlertsPanel
            alerts={live.alerts}
            loading={live.loading}
            ackingId={ackingId}
            onAcknowledge={(id) => void acknowledge(id)}
          />
        </Card>
      </div>
    </div>
  );
}
